import { useEffect, useRef, useState } from 'react';

import { pedir } from '../lib/api';
import { baseApi } from '../lib/servidor';
import { almacenToken } from '../lib/api';
import { VistaEnVivo } from './VistaEnVivo';

/**
 * El video en vivo de la charola, por WebRTC.
 *
 * El kiosco y este navegador montan una conexión de video **directa** en la
 * LAN: la imagen no pasa por el servidor, va de un lado al otro con baja
 * latencia. El API solo hace de buzón para el apretón de manos (la oferta y la
 * respuesta SDP).
 *
 * Con red al kiosco por candidatos «host», no hace falta STUN ni TURN. El día
 * que el guardia esté en la nube habrá que añadir un TURN aquí; en la caseta,
 * los dos están en la misma red y se encuentran solos.
 *
 * Y si nada de esto conecta —un navegador que no coopera, una red rara— cae al
 * video por cuadros JPEG que ya existía (`VistaEnVivo`). Nunca una pantalla
 * negra: para el guardia, ver algo importa más que la elegancia del transporte.
 */

/** Cuánto se espera a que WebRTC conecte antes de rendirse al respaldo. */
const ESPERA_CONEXION_MS = 8000;

/** Cada cuánto se pregunta por la respuesta del kiosco durante el apretón. */
const SONDEO_RESPUESTA_MS = 400;

type Estado = 'conectando' | 'en-vivo' | 'respaldo';

export function VideoGuardia({ accesoId }: { accesoId: string }) {
  const [estado, setEstado] = useState<Estado>('conectando');
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let vivo = true;
    let pc: RTCPeerConnection | null = null;
    let sondeo: ReturnType<typeof setTimeout> | null = null;
    let plazo: ReturnType<typeof setTimeout> | null = null;

    async function esperarCandidatos(conexion: RTCPeerConnection): Promise<void> {
      // ICE sin trickle: se reúnen todos los candidatos antes de mandar la
      // oferta, para que viaje completa y no haga falta un canal aparte.
      if (conexion.iceGatheringState === 'complete') return;
      await new Promise<void>((resolver) => {
        function revisar() {
          if (conexion.iceGatheringState === 'complete') {
            conexion.removeEventListener('icegatheringstatechange', revisar);
            resolver();
          }
        }
        conexion.addEventListener('icegatheringstatechange', revisar);
        // Tope por si el navegador se atasca reuniendo candidatos.
        setTimeout(resolver, 3000);
      });
    }

    async function negociar(): Promise<void> {
      pc = new RTCPeerConnection();

      // Dos huecos de video recvonly: soportan hasta dos cámaras (charola y
      // rostro) aunque hoy el kiosco solo mande una. Los que sobren quedan
      // inactivos, sin romper nada.
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('video', { direction: 'recvonly' });

      pc.ontrack = (e) => {
        if (videoRef.current && e.streams[0]) {
          videoRef.current.srcObject = e.streams[0];
        }
      };

      pc.onconnectionstatechange = () => {
        if (!pc) return;
        if (pc.connectionState === 'connected') {
          if (plazo) clearTimeout(plazo);
          if (vivo) setEstado('en-vivo');
        } else if (pc.connectionState === 'failed') {
          if (vivo) setEstado('respaldo');
        }
      };

      const oferta = await pc.createOffer();
      await pc.setLocalDescription(oferta);
      await esperarCandidatos(pc);
      if (!vivo) return;

      await pedir(`/accesos/${accesoId}/webrtc/oferta`, {
        metodo: 'POST',
        cuerpo: { sdp: pc.localDescription?.sdp ?? '' },
      });

      // Se sondea la respuesta del kiosco. El endpoint da 204 hasta que llega.
      async function traerRespuesta(): Promise<void> {
        if (!vivo || !pc) return;
        try {
          const r = await fetch(`${baseApi()}/api/accesos/${accesoId}/webrtc/respuesta`, {
            headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
            cache: 'no-store',
          });
          if (r.status === 200) {
            const { sdp } = (await r.json()) as { sdp: string };
            await pc.setRemoteDescription({ type: 'answer', sdp });
            return; // conexión en marcha; el resto lo hace onconnectionstatechange
          }
        } catch {
          // se reintenta abajo
        }
        if (vivo) sondeo = setTimeout(() => void traerRespuesta(), SONDEO_RESPUESTA_MS);
      }
      void traerRespuesta();
    }

    // Si en unos segundos no hubo conexión, se cae al JPEG. El plazo se cancela
    // en cuanto la conexión pasa a "connected".
    plazo = setTimeout(() => {
      if (vivo && estado !== 'en-vivo') setEstado('respaldo');
    }, ESPERA_CONEXION_MS);

    negociar().catch(() => {
      if (vivo) setEstado('respaldo');
    });

    return () => {
      vivo = false;
      if (sondeo) clearTimeout(sondeo);
      if (plazo) clearTimeout(plazo);
      if (pc) {
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.close();
      }
    };
    // El apretón se rehace solo si cambia el acceso; `estado` no debe reiniciarlo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accesoId]);

  // El respaldo es el componente JPEG de siempre, tal cual.
  if (estado === 'respaldo') return <VistaEnVivo accesoId={accesoId} />;

  return (
    <figure className="relative overflow-hidden rounded-xl border border-outline-variant bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="h-64 w-full bg-black object-contain"
      />
      {estado === 'conectando' && (
        <span className="absolute inset-x-0 bottom-0 bg-surface-container-high px-3 py-1.5 text-center text-label-bold uppercase tracking-wider text-on-surface-variant">
          Conectando video…
        </span>
      )}
    </figure>
  );
}
